import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Title, Text, Card, Badge, Group, Stack, Button,
  Tabs, Avatar, Progress, Divider, Skeleton,
  ActionIcon, Tooltip, Loader, Center, Box, Modal, Menu, ColorSwatch
} from '@mantine/core';
import { IconArrowLeft, IconUsers, IconCalendar,
  IconBooks, IconNotes,
  IconRefresh, IconClipboardCheck, IconMessageQuestion,
  IconEye,
  IconBabyCarriage, IconChevronDown, IconArrowsExchange
} from '@tabler/icons-react';
import {
  useFacilitatorPortalService, useRegistrationStatusService, RegistrationStatus,
  FacilitatorRegistrationView, FacilitatorSessionView,
  RegistrationCourseCustomDataModelView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ChildrenTab, { parseChildren } from '../../Components/ChildrenTab';
import CourseFilesTab from './Tabs/CourseFilesTab';
import NotesTab from './Tabs/NotesTab';
import MembersTab from './Tabs/MembersTab';
import AdditionalDetailsTab from './Tabs/AdditionalDetailsTab';
import { tabStyle } from '@/components/ui/TabStyle';
import { useSessionTabs } from './Hooks/useSessionTabs';
import SpecialSessionsTabContent from './SpecialSession/SpecialSessionsTabContent';
import RegistrationSessionDetailsModal from './Components/RegistrationSessionDetailsModal';
import SessionStateMenu from './Components/SessionStateMenu';
import FacilitatorPortalNotice from '../Components/FacilitatorPortalNotice';
import { useAuth } from '@/hooks/useEnrichedAuth';

// Static tabs keep their fixed keys; dynamic session tabs use whatever
// key buildSessionTabs() produced ('sessions', or `special-<id>`).
type StaticTabKey = 'members' | 'facilitators' | 'additionalDetails' | 'notes' | 'courseFiles';
type TabKey = StaticTabKey | string;

export default function MyRegistrationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const facilitatorPortalService = useFacilitatorPortalService();
  const statusService = useRegistrationStatusService();

  const [registration, setRegistration] = useState<FacilitatorRegistrationView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('members');
  const [detailsSession, setDetailsSession] = useState<FacilitatorSessionView | null>(null);
  const [childrenRow, setChildrenRow] = useState<RegistrationCourseCustomDataModelView | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statuses, setStatuses]           = useState<RegistrationStatus[]>([]);
  const [pendingStatus, setPendingStatus] = useState<RegistrationStatus | null>(null);
  const [changing, setChanging]           = useState(false);
  const { user } = useAuth();

  const openSessionDetails = (session: FacilitatorSessionView) => {
    setDetailsSession(session);
    setDetailsOpen(true);
  };

  const registrationID = Number(id);

  // ── Dynamic session tabs (special sessions + one combined "Sessions" tab) ─
  // NOTE: FacilitatorRegistrationView has no courseID, so once useSessionTabs
  // is switched to the mySessions()-driven approach it should key off
  // registrationID alone (orderNumber now comes from FacilitatorSessionView,
  // so the courseID -> course catalogue lookup this hook used for orderNo is
  // no longer needed here).
  const {
    tabs: sessionTabs,
    loading: sessionTabsLoading,
    refetch: refetchSessionTabs,
  } = useSessionTabs(
    registrationID,
    true // fetched once on mount, independent of which tab is active
  );

  // All sessions across every tab (special + normal), for the combined "Sessions" tab.
  const allSessions = useMemo(() => {
    return sessionTabs.flatMap((tab) =>
      tab.kind === 'special' ? [tab.session] : tab.sessions
    );
  }, [sessionTabs]);

  // Facilitators can only change progress/status via the pill menus — refetch
  // to pick up the change since allSessions is derived from the hook's state.
  const handleSessionStateUpdated = useCallback(() => {
    refetchSessionTabs();
  }, [refetchSessionTabs]);

   // ── Fetch registration statuses ─────────────────────────────────────────
  useEffect(() => {
  (async () => {
    try {
      const s = await statusService.getAll();
      setStatuses((s as any)?.data ?? s ?? []);
    } catch (e) {
      console.error('Error fetching registration statuses:', e);
    }
  })();
}, [statusService]);

  // ── Fetch registration (facilitator-scoped view) ──────────────────────────
  const fetchRegistration = useCallback(async () => {
    if (!registrationID) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await facilitatorPortalService.myRegistration(registrationID);
      setRegistration(res ?? null);
    } catch (e) {
      // A 403 here means the account has no facilitator record — show the inline notice rather
      // than a misleading "Registration not found".
      console.error('Error loading registration:', e);
      setLoadError(e);
      setRegistration(null);
    } finally {
      setLoading(false);
    }
  }, [facilitatorPortalService, registrationID]);

  useEffect(() => { fetchRegistration(); }, [fetchRegistration]);

   // ── Fetch children data ──────────────────────────────────────────────────
  const fetchChildrenData = useCallback(async () => {
  if (!registrationID) return;
  try {
    // Children are stored in the same course custom-data collection rendered by
    // AdditionalDetailsTab. The facilitator portal exposes that collection as courseCustomData.
    const res: unknown = await facilitatorPortalService.courseCustomData(registrationID);
    const data: RegistrationCourseCustomDataModelView[] = Array.isArray(res)
      ? res as RegistrationCourseCustomDataModelView[]
      : (res as { data?: RegistrationCourseCustomDataModelView[] })?.data ?? [];
    const found = data.find((r) => r.field?.fieldKey === 'children') ?? null;
    setChildrenRow(found && parseChildren(found).length > 0 ? found : null);
  } catch (e) {
    console.error('Error fetching children data:', e);
  }
}, [facilitatorPortalService, registrationID]);

useEffect(() => { fetchChildrenData(); }, [fetchChildrenData]);

//
const handleConfirmStatusChange = async () => {
  if (!pendingStatus) return;
  setChanging(true);
  try {
    // ⚠️ placeholder method name, replace with the real one
    await facilitatorPortalService.changeRegistrationStatus(
      registrationID, Number(pendingStatus.statusID),
    );
    toast.success('Status updated', `Registration is now "${pendingStatus.name}".`);
    setPendingStatus(null);
    await fetchRegistration();
  } catch (e) {
    console.error('Error changing registration status:', e);
    toast.error('Error', 'Failed to change the registration status.');
  } finally {
    setChanging(false);
  }
};

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading && !registration) {
    return (
      <Container size="xl" py="xl">
        <Skeleton height={36} width={220} mb="md" />
        <Skeleton height={140} radius="md" mb="md" />
        <Skeleton height={400} radius="md" />
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container size="xl" py="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/Grow/Enrolment')}
          mb="md"
        >
          Back to Registrations
        </Button>
        <FacilitatorPortalNotice error={loadError} subject="this registration" onRetry={fetchRegistration} />
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container size="xl" py="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/Grow/Enrolment')}
          mb="md"
        >
          Back to Registrations
        </Button>
        <Text c="dimmed">Registration not found.</Text>
      </Container>
    );
  }

  const statusColor = registration.statusColor ?? undefined;
  const statusName  = registration.statusName  ?? '—';
  const courseName  = registration.courseName ?? `Registration ${registration.registrationID}`;

  return (
    <Container size="xl" py="xl">
      {/* ── Back navigation ── */}
      <Button
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        onClick={() => navigate('/Grow/Enrolment')}
        mb="md"
      >
        Back to Registrations
      </Button>

      {/* ── Header row: Title + Edit ── */}
      <Group justify="space-between" mb="sm">
       <Title order={2}>Registration Details</Title>
       <Group gap={6}>
       <Tooltip label="Refresh">
                   <ActionIcon
                     variant="subtle"
                     onClick={() => { fetchRegistration(); refetchSessionTabs(); }}
                     loading={loading}
                     size="lg"
                   >
                     <IconRefresh size={16} />
                   </ActionIcon>
                 </Tooltip>

                 <Group gap={6}>

  <Menu shadow="md" width={220} position="bottom-end">
    <Menu.Target>
      <Button
        variant="light"
        leftSection={<IconArrowsExchange size={16} />}
        rightSection={<IconChevronDown size={14} />}
      >
        Change Status
      </Button>
    </Menu.Target>
    <Menu.Dropdown>
      <Menu.Label>Move registration to</Menu.Label>
      {statuses.map((s) => (
        <Menu.Item
          key={s.statusID}
          disabled={s.name === registration.statusName}
          leftSection={<ColorSwatch color={s.color || '#868e96'} size={14} />}
          onClick={() => setPendingStatus(s)}
        >
          {s.name}
        </Menu.Item>
      ))}
    </Menu.Dropdown>
  </Menu>
</Group>
    
        </Group>
      </Group>

      {/* ── Profile card ── */}
      <Card withBorder radius="md" padding="lg" mb="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Group align="flex-start" gap="md">
            <Avatar color={registration.courseColor ?? 'blue'} radius="md" size={80}>
              
              <IconBooks size={28} />
            </Avatar>

            <div>
              <Group gap="xs" align="center">
                <Title order={3}>{courseName}</Title>
                <Badge
                  variant="light"
                  style={statusColor ? { backgroundColor: `${statusColor}22`, color: statusColor } : undefined}
                  color={statusColor ? undefined : 'gray'}
                >
                  {statusName}
                </Badge>
                {registration.isLead && (
                  <Badge variant="light" color="blue">Lead</Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {registration.locationName ?? (registration.isRemote ? 'Remote' : '—')}
              </Text>
              <Text size="sm" c="dimmed">
                {registration.periodDisplay}
              </Text>
            </div>
          </Group>

          <Group gap="xl">
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Participants</Text>
              <Group gap={4}>
                <IconUsers size={16} />
                <Text fw={600}>{registration.participantCount ?? 0}</Text>
              </Group>
            </Stack>
            <Stack gap={2} align="center">
              <Text size="xs" c="dimmed">Next Session</Text>
              <Text fw={600}>{registration.nextSessionDisplay ?? '—'}</Text>
            </Stack>
          </Group>
        </Group>

        {typeof registration.progressPercent === 'number' && (
          <>
            <Divider my="sm" />
            <Group justify="space-between" mb={4}>
              <Text size="xs" c="dimmed">Progress</Text>
              <Text size="xs" c="dimmed">{registration.progressPercent}%</Text>
            </Group>
            <Progress
              value={registration.progressPercent}
              color="blue"
              radius="xl"
            />
          </>
        )}
      </Card>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={(v) => setActiveTab((v as TabKey) ?? 'members')} keepMounted={false}>
        <Tabs.List mb="md">

          <Tabs.Tab value="members" leftSection={<IconUsers size={16} />} style={tabStyle(activeTab === 'members')}>Members</Tabs.Tab>
          

          {/* ── Dynamic session tabs: one per special session, plus one combined "Sessions" tab ── */}
          {sessionTabsLoading && sessionTabs.length === 0 && (
            <Center px="md">
              <Loader size="xs" />
            </Center>
          )}
          {sessionTabs.map((tab) => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              leftSection={tab.kind === 'special' ? <IconClipboardCheck size={16} /> : <IconCalendar size={16} />}
              style={tabStyle(activeTab === tab.key)}
            >
              {tab.title}
            </Tabs.Tab>
          ))}

          <Tabs.Tab value="additionalDetails" leftSection={<IconMessageQuestion size={16} />} style={tabStyle(activeTab === 'additionalDetails')}>Additional Details</Tabs.Tab>
          {childrenRow && (
            <Tabs.Tab value="children" leftSection={<IconBabyCarriage size={16} />} style={tabStyle(activeTab === 'children')}>
              Children
            </Tabs.Tab>
          )}
          <Tabs.Tab value="notes" leftSection={<IconNotes size={16} />} style={tabStyle(activeTab === 'notes')}>Notes</Tabs.Tab>
          <Tabs.Tab value="courseFiles" leftSection={<IconBooks size={16} />} style={tabStyle(activeTab === 'courseFiles')}>Course Files</Tabs.Tab>
        </Tabs.List>

        <Card withBorder radius="md" padding="lg" mt="sm">

          <Tabs.Panel value="members">
            <MembersTab
              registrationId={registrationID}
              active={activeTab === 'members'}
            />
          </Tabs.Panel>

          {/* ── Dynamic session tab panels ── */}
          {sessionTabs.map((tab) => (
            <Tabs.Panel key={tab.key} value={tab.key}>
              {tab.kind === 'special' ? (
                <SpecialSessionsTabContent
                  registrationID={registrationID}
                  registrationSessionID={tab.session.registrationSessionID}
                />
              ) : (
                <Stack gap="md">
                  <Group justify="flex-end" gap={4}>
                    <Tooltip label="Refresh sessions" withArrow>
                      <ActionIcon
                        variant="subtle"
                        onClick={refetchSessionTabs}
                        loading={sessionTabsLoading}
                        size="lg"
                      >
                        <IconRefresh size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Stack gap={0}>
                    <Group justify="space-between" px="sm" pb={6} align="center">
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" c="dimmed" fw={500} tt="uppercase">Session</Text>
                      </Box>
                      <Group gap="xs" align="center">
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 130, textAlign: 'left' }}>Date</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Progress</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Status</Text>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ minWidth: 90, textAlign: 'center' }}>Actions</Text>
                      </Group>
                    </Group>
                    <Divider mb={4} />
                    {allSessions.map((s) => {
                    return (
                      <Group
                        key={s.registrationSessionID}
                        justify="space-between"
                        align="flex-start"
                        py="sm"
                        px="sm"
                        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                      >   
                        <Stack gap={2}>
                          <Group gap={6}>
                            <Text size="sm" fw={500}>{s.name ?? '—'}</Text>
                            {s.isSpecial && (
                              <Badge size="xs" variant="light" color="grape">Special</Badge>
                            )}
                            {s.isToday && (
                              <Badge size="xs" variant="filled" color="blue">Today</Badge>
                            )}
                          </Group>
                        </Stack>

                        <Group gap="xs" align="center">
                          <Group gap={4} style={{ minWidth: 130 }}>
                            <IconCalendar size={14} style={{ opacity: 0.6 }} />
                            <Text size="xs" c="dimmed">{s.sessionDateDisplay ?? '—'}</Text>
                          </Group>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateMenu
                              session={s}
                              kind="progress"
                              onUpdated={handleSessionStateUpdated}
                            />
                          </Box>

                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <SessionStateMenu
                              session={s}
                              kind="status"
                              onUpdated={handleSessionStateUpdated}
                            />
                          </Box>
                          <Box style={{ minWidth: 90, textAlign: 'center' }}>
                            <Tooltip label="View session">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => openSessionDetails(s)}
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Box>
                        </Group>
                      </Group>
                    );
                  })}
                  </Stack>
                </Stack>
              )}
            </Tabs.Panel>
          ))}

          <Tabs.Panel value="additionalDetails">
            <AdditionalDetailsTab
              registrationId={registrationID}
              active={activeTab === 'additionalDetails'}
            />
          </Tabs.Panel>
            {/* Children Tab */}
       {childrenRow && (
          <Tabs.Panel value="children">
            <ChildrenTab
              registrationId={registrationID}
              active={activeTab === 'children'}
              row={childrenRow}
              onChanged={fetchChildrenData}
              canDelete={false}
            />
          </Tabs.Panel>
        )}
          <Tabs.Panel value="notes">
            <NotesTab
              registrationId={registrationID}
              active={activeTab === 'notes'}
              currentUserId={user?.id ?? undefined}
            />
          </Tabs.Panel>
          <Tabs.Panel value="courseFiles">
            <CourseFilesTab
              registrationId={registrationID}
              active={activeTab === 'courseFiles'}
              onRegistrationChange={fetchRegistration}
            />
          </Tabs.Panel>
        </Card>
      </Tabs>
       
        {/* ── Session Details Modal ── */}
          {detailsSession && (
            <RegistrationSessionDetailsModal
              opened={detailsOpen}
              onClose={() => {
                setDetailsOpen(false);
                setDetailsSession(null);
              }}
              session={detailsSession}
              currentUserId={user?.id ?? undefined}
            />
          )}

          <Modal
  opened={!!pendingStatus}
  onClose={() => !changing && setPendingStatus(null)}
  title="Change registration status"
  centered
>
  <Stack>
    <Text size="sm">Change status from:</Text>
    <Group gap="xs">
      <Badge
        variant="filled"
        style={statusColor ? { backgroundColor: statusColor } : undefined}
        color={statusColor ? undefined : 'gray'}
      >
        {statusName}
      </Badge>
      <Text size="sm">→</Text>
      {pendingStatus && (
        <Badge variant="filled" style={{ backgroundColor: pendingStatus.color || '#868e96' }}>
          {pendingStatus.name}
        </Badge>
      )}
    </Group>
    <Group justify="flex-end">
      <Button variant="default" onClick={() => setPendingStatus(null)} disabled={changing}>
        Cancel
      </Button>
      <Button onClick={handleConfirmStatusChange} loading={changing}>
        Confirm
      </Button>
    </Group>
  </Stack>
</Modal>

    </Container>
  );
}